// import React, { useState, useCallback, useEffect } from "react";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// import { paystackInitialize, verifyPaystack, getDVA, generateDVA } from "../../../api/apiServices";
// import { Copy, Building2, CreditCard } from "lucide-react";
// import CompleteProfilePrompt from "../../../components/CompleteProfilePrompt";
// import Toast from "../../../components/Toast";
// import { useAuth } from "../../../context/useAuth";
// import { useProfileCompletion } from "../../../hooks/useProfileCompletion";

// const quickAmounts = [200, 500, 1000, 2000, 5000, 10000, 20000];

// const ALLOWED_BANKS = [
//   "Wema Bank",
//   "Titan Paystack"
// ];

// export default function AddFundPageOne() {
//   const navigate = useNavigate();

//   const [selectedAmount, setSelectedAmount] = useState(null);
//   const [customAmount, setCustomAmount] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [successMessage, setSuccessMessage] = useState("");
//   const [dva, setDva] = useState(null);
//   const [dvaLoading, setDvaLoading] = useState(true);
//   const [creatingDva, setCreatingDva] = useState(false);
//   const [toast, setToast] = useState(null);

//   // `authLoading` covers the brief window where AuthContext is still
//   // restoring `user` from localStorage on first mount. Without checking
//   // it, the prompt could flash open for a split second even for someone
//   // with a complete profile, since `user` starts out null.
//   const { user, loading: authLoading } = useAuth();
//   const { isComplete, missingFields } = useProfileCompletion(user);

//   const [showWebView, setShowWebView] = useState(false);
//   const [authorizationUrl, setAuthorizationUrl] = useState("");
//   const [transactionReference, setTransactionReference] = useState("");

//   const finalAmount = selectedAmount || (customAmount ? Number(customAmount) : 0);
//   const isValidAmount = finalAmount >= 100 && finalAmount <= 200000;
//   const canProceed = isValidAmount && !loading;

//   const showToast = (type, message) => {
//     setToast({ type, message });
//     setTimeout(() => setToast(null), 4500);
//   };

//   useEffect(() => {
//     loadDVA();
//   }, []);

//   async function loadDVA() {
//     try {
//       setDvaLoading(true);
//       const res = await getDVA();
//       if (res?.data) setDva(res.data);
//     } catch (err) {
//       console.log(err);
//     } finally {
//       setDvaLoading(false);
//     }
//   }

//   async function handleCreateDVA() {
//     try {
//       setCreatingDva(true);
//       const res = await generateDVA("wema-bank");
//       if (res?.data) setDva(res.data);
//       showToast("success", "Virtual account created successfully");
//     } catch (err) {
//       showToast(
//         "error",
//         err?.response?.data?.message || "Unable to create virtual account"
//       );
//     } finally {
//       setCreatingDva(false);
//     }
//   }

//   function copyAccountNumber(number) {
//     navigator.clipboard.writeText(number);
//     showToast("success", "Account number copied");
//   }

//   const handleQuickSelect = (amount) => {
//     setCustomAmount(amount.toString());
//     setError("");
//   };

//   const handleCustomChange = (e) => {
//     const value = e.target.value.replace(/[^0-9]/g, "");
//     setCustomAmount(value);
//     setSelectedAmount(null);
//     setError("");
//   };

//   const handleFund = useCallback(async () => {
//     if (!finalAmount || finalAmount < 100) {
//       setError("Minimum amount is ₦100");
//       return;
//     }
//     if (finalAmount > 200000) {
//       setError("Maximum amount is ₦200,000");
//       return;
//     }

//     setLoading(true);
//     setError("");

//     try {
//       const res = await paystackInitialize(finalAmount);
//       if (res?.authorization_url && res?.reference) {
//         setTransactionReference(res.reference);
//         setAuthorizationUrl(res.authorization_url);
//         setShowWebView(true);
//       } else {
//         setError("Failed to initialize payment. Please try again.");
//       }
//     } catch (err) {
//       setError(err?.response?.data?.message || "Unable to start payment. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   }, [finalAmount]);

//   const handlePaymentClose = async () => {
//     setShowWebView(false);

//     if (!transactionReference) {
//       setError("Payment was cancelled.");
//       return;
//     }

//     setLoading(true);

//     try {
//       const verifyRes = await verifyPaystack(transactionReference);

//       if (
//         verifyRes?.status === "success" ||
//         verifyRes?.message?.toLowerCase().includes("success")
//       ) {
//         setSuccessMessage("✅ Wallet funded successfully!");
//         setSelectedAmount(null);
//         setCustomAmount("");
//         setTransactionReference("");

//         setTimeout(() => {
//           navigate("/dashboard");
//         }, 1500);
//       } else {
//         setError("Payment could not be verified. Please check your transaction history.");
//       }
//     } catch (err) {
//       console.error("Verification error:", err);
//       setError("Unable to verify payment. Please check your transaction history.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 20 }}
//       animate={{ opacity: 1, y: 0 }}
//       className="min-h-screen bg-gray-50 pb-24"
//     >
//       {/* Header */}
//       <div className="bg-white border-b px-4 py-4 flex items-center justify-between sticky top-0 z-10">
//         <button onClick={() => navigate(-1)} className="text-2xl text-gray-700">←</button>
//         <h1 className="font-semibold text-lg">Add Funds</h1>
//         <div
//           className="text-sm text-[#3866A3] font-medium cursor-pointer"
//           onClick={() => navigate("/history")}
//         >
//           History
//         </div>
//       </div>

//       <CompleteProfilePrompt open={!authLoading && !isComplete} missingFields={missingFields} />

//       {/* Toast */}
//       <Toast toast={toast} onClose={() => setToast(null)} />

//       {/* Virtual Account Card */}
//       <div className="p-4">
//         <div className="bg-gradient-to-r from-[#3866A3] to-[#4F82C3] rounded-2xl shadow-sm p-5 text-white">
//           <div className="flex items-center gap-3 mb-4">
//             <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
//               <CreditCard size={22} />
//             </div>
//             <div>
//               <h3 className="font-semibold text-lg">Virtual Account</h3>
//               <p className="text-sm text-white/80">Fund your wallet instantly</p>
//             </div>
//           </div>

//           {dvaLoading ? (
//             <div className="py-4 text-sm">Loading account...</div>
//           ) : dva ? (
//             <div className="space-y-3">
//               <div>
//                 <p className="text-xs text-white/70">Account Number</p>
//                 <div className="flex items-center justify-between mt-1">
//                   <p className="text-2xl font-bold tracking-wider">{dva.accountNumber}</p>
//                   <button
//                     onClick={() => copyAccountNumber(dva.accountNumber)}
//                     className="bg-white/20 p-2 rounded-lg"
//                   >
//                     <Copy size={16} />
//                   </button>
//                 </div>
//               </div>
//               <div className="grid grid-cols-2 gap-3 pt-2">
//                 <div>
//                   <p className="text-xs text-white/70">Bank</p>
//                   <p className="font-medium">{dva.bankName}</p>
//                 </div>
//                 <div>
//                   <p className="text-xs text-white/70">Account Name</p>
//                   <p className="font-medium truncate">{dva.accountName}</p>
//                 </div>
//               </div>
//             </div>
//           ) : (
//             <div className="space-y-4">
//               <div className="flex items-center gap-3">
//                 <Building2 size={22} />
//                 <p className="text-sm">No virtual account has been created yet.</p>
//               </div>
//               <button
//                 onClick={handleCreateDVA}
//                 disabled={creatingDva}
//                 className="w-full bg-white text-[#3866A3] font-semibold py-3 rounded-xl"
//               >
//                 {creatingDva ? "Creating Account..." : "Generate Virtual Account"}
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Amount Selector */}
//       <div className="px-4">
//         <div className="bg-white rounded-2xl p-6 shadow-sm">
//           <p className="text-lg font-semibold mb-6 text-center">How much do you want to add?</p>

//           {/* Quick Amounts */}
//           <div className="grid grid-cols-3 gap-3 mb-8">
//             {quickAmounts.map((amt) => (
//               <motion.button
//                 key={amt}
//                 whileTap={{ scale: 0.95 }}
//                 onClick={() => handleQuickSelect(amt)}
//                 className={`py-4 rounded-2xl font-semibold transition-all text-sm ${
//                   customAmount === amt.toString()
//                     ? "bg-[#3866A3] text-white shadow-md"
//                     : "bg-gray-100 hover:bg-gray-200 text-gray-800"
//                 }`}
//               >
//                 ₦{amt.toLocaleString()}
//               </motion.button>
//             ))}
//           </div>

//           {/* Custom Amount */}
//           <div className="mb-6">
//             <label className="block text-sm text-gray-600 mb-2">Or enter custom amount</label>
//             <div className="relative">
//               <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₦</span>
//               <input
//                 type="text"
//                 inputMode="numeric"
//                 placeholder="0.00"
//                 value={customAmount}
//                 onChange={handleCustomChange}
//                 className="w-full pl-9 pr-4 py-4 border border-gray-300 rounded-2xl text-lg focus:outline-none focus:border-[#3866A3]"
//               />
//             </div>
//           </div>

//           {error && (
//             <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm text-center">
//               {error}
//             </div>
//           )}

//           {successMessage && (
//             <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm text-center">
//               {successMessage}
//             </div>
//           )}

//           <p className="text-xs text-gray-500 text-center mt-2">
//             Minimum: ₦100 • Maximum: ₦200,000
//           </p>
//         </div>

//         <motion.button
//           whileTap={{ scale: canProceed ? 0.97 : 1 }}
//           onClick={handleFund}
//           disabled={!canProceed || loading}
//           className={`mt-6 w-full py-4 rounded-2xl font-semibold text-lg transition-all ${
//             canProceed && !loading
//               ? "bg-[#3866A3] hover:bg-[#2d5a8c] text-white"
//               : "bg-gray-300 text-gray-500 cursor-not-allowed"
//           }`}
//         >
//           {loading ? "Initializing Payment..." : "Proceed to Paystack"}
//         </motion.button>
//       </div>

//       {/* Paystack WebView Modal */}
//       {showWebView && authorizationUrl && (
//         <div className="fixed inset-0 bg-white z-50 flex flex-col">
//           <div className="bg-white border-b p-4 flex justify-between items-center">
//             <h3 className="font-medium">Complete Payment</h3>
//             <button onClick={handlePaymentClose} className="text-red-600 font-medium">
//               Close
//             </button>
//           </div>
//           <iframe
//             src={authorizationUrl}
//             className="flex-1 w-full border-none"
//             title="Paystack Payment"
//             sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
//           />
//         </div>
//       )}
//     </motion.div>
//   );
// }


import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getDVA, generateDVA } from "../../../api/apiServices";
import { Copy, Building2, CreditCard } from "lucide-react";
import CompleteProfilePrompt from "../../../components/CompleteProfilePrompt";
import Toast from "../../../components/Toast";
import { useAuth } from "../../../context/useAuth";
import { useProfileCompletion } from "../../../hooks/useProfileCompletion";

export default function AddFundPageOne() {
  const navigate = useNavigate();

  const [dva, setDva] = useState(null);
  const [dvaLoading, setDvaLoading] = useState(true);
  const [creatingDva, setCreatingDva] = useState(false);
  const [toast, setToast] = useState(null);

  // `authLoading` covers the brief window where AuthContext is still
  // restoring `user` from localStorage on first mount. Without checking
  // it, the prompt could flash open for a split second even for someone
  // with a complete profile, since `user` starts out null.
  const { user, loading: authLoading } = useAuth();
  const { isComplete, missingFields } = useProfileCompletion(user);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4500);
  };

  useEffect(() => {
    loadDVA();
  }, []);

  async function loadDVA() {
    try {
      setDvaLoading(true);
      const res = await getDVA();
      if (res?.data) setDva(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setDvaLoading(false);
    }
  }

  async function handleCreateDVA() {
    try {
      setCreatingDva(true);
      const res = await generateDVA("wema-bank");
      if (res?.data) setDva(res.data);
      showToast("success", "Virtual account created successfully");
    } catch (err) {
      showToast(
        "error",
        err?.response?.data?.message || "Unable to create virtual account"
      );
    } finally {
      setCreatingDva(false);
    }
  }

  function copyAccountNumber(number) {
    navigator.clipboard.writeText(number);
    showToast("success", "Account number copied");
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gray-50 pb-24"
    >
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-2xl text-gray-700">←</button>
        <h1 className="font-semibold text-lg">Add Funds</h1>
        <div
          className="text-sm text-[#3866A3] font-medium cursor-pointer"
          onClick={() => navigate("/history")}
        >
          History
        </div>
      </div>

      <CompleteProfilePrompt open={!authLoading && !isComplete} missingFields={missingFields} />

      {/* Toast */}
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Virtual Account Card */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-[#3866A3] to-[#4F82C3] rounded-2xl shadow-sm p-5 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <CreditCard size={22} />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Virtual Account</h3>
              <p className="text-sm text-white/80">Fund your wallet instantly</p>
            </div>
          </div>

          {dvaLoading ? (
            <div className="py-4 text-sm">Loading account...</div>
          ) : dva ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs text-white/70">Account Number</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-2xl font-bold tracking-wider">{dva.accountNumber}</p>
                  <button
                    onClick={() => copyAccountNumber(dva.accountNumber)}
                    className="bg-white/20 p-2 rounded-lg"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <p className="text-xs text-white/70">Bank</p>
                  <p className="font-medium">{dva.bankName}</p>
                </div>
                <div>
                  <p className="text-xs text-white/70">Account Name</p>
                  <p className="font-medium truncate">{dva.accountName}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Building2 size={22} />
                <p className="text-sm">No virtual account has been created yet.</p>
              </div>
              <button
                onClick={handleCreateDVA}
                disabled={creatingDva}
                className="w-full bg-white text-[#3866A3] font-semibold py-3 rounded-xl"
              >
                {creatingDva ? "Creating Account..." : "Generate Virtual Account"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      {!dvaLoading && dva && (
        <div className="px-4">
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <p className="text-sm text-gray-600">
              Fund your wallet with the details above and your wallet
              will be credited automatically once payment is confirmed.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}